import {
    MetricsService,
    PropertyMap,
    UserAction,
    userActionTypeEnum,
} from '@thoughtspot/metrics';
import React from 'react';
import { AnswerMode, AnswerPageContext } from '/@services/answer/answer.util';
import {
    DisplayMode,
    QuerableDataSourceType,
    SagePhrase,
} from '/@services/generated/graphql-types';
import { useApolloClient } from '/@utils/error-handling/apollo-hooks-wrappers/custom-apollo-hooks';

/**
 * Property builders, mappers, and the token-change classifier for the two
 * query-cancellation Mixpanel events: `query_initiated` and `query_cancelled`.
 *
 * Both events are keyed on `query_id` = the in-flight `cancelId`, so they pair
 * per CDW query and power the cancellation ratio
 * `COUNT(query_cancelled) / COUNT(query_initiated)`.
 *
 * Identity/org/cluster/time are omitted — Mixpanel attaches them globally.
 *
 * NOTE: Mixpanel's `PropertyMap` value type forbids `null`. Absent values are
 * coerced to `''` (empty string) — that is the sentinel for "no value",
 * including the no-in-flight-query `query_id` case.
 */

export enum CancelType {
    IMPLICIT = 'implicit',
    EXPLICIT = 'explicit',
}

export enum TriggerAction {
    TOKEN_ADDED = 'token_added',
    TOKEN_REMOVED = 'token_removed',
    TOKEN_EDITED = 'token_edited',
    DATASOURCE_CHANGED = 'datasource_changed',
    X_BUTTON_CLICKED = 'x_button_clicked',
    CANCEL_BUTTON_CLICKED = 'cancel_button_clicked',
    NAVIGATION = 'navigation',
    PAGE_HIDDEN = 'page_hidden',
}

enum CdwType {
    SNOWFLAKE = 'snowflake',
    BIGQUERY = 'bigquery',
    REDSHIFT = 'redshift',
    OTHER = '',
}

enum PageContext {
    SEARCH_DATA = 'search_data',
    ANSWER = 'answer',
    LIVEBOARD = 'liveboard',
    UNMAPPED = '',
}

enum EntryPath {
    DIRECT = 'direct',
    LIVEBOARD_EDIT = 'liveboard_edit',
    SPOTTER_EDIT = 'spotter_edit',
    SAVED_ANSWER_EDIT = 'saved_answer_edit',
}

export enum VizType {
    CHART = 'chart',
    TABLE = 'table',
    HEADLINE = 'headline',
    UNMAPPED = '',
}

/** Maps the queryable data source to the three CDW buckets; '' for anything else. */
export const mapCdwType = (
    queryableDataSource?: QuerableDataSourceType | null,
): CdwType => {
    switch (queryableDataSource) {
        case QuerableDataSourceType.RdbmsSnowflake:
            return CdwType.SNOWFLAKE;
        case QuerableDataSourceType.RdbmsGcpBigquery:
            return CdwType.BIGQUERY;
        case QuerableDataSourceType.RdbmsRedshift:
            return CdwType.REDSHIFT;
        default:
            return CdwType.OTHER;
    }
};

/** Buckets the AnswerPageContext into the analytics page_context; '' when unmapped. */
export const mapPageContext = (
    pageContext?: AnswerPageContext | null,
): PageContext => {
    switch (pageContext) {
        case AnswerPageContext.UNSAVED_ANSWER:
            return PageContext.SEARCH_DATA;
        case AnswerPageContext.SAVED_ANSWER:
        case AnswerPageContext.VIEW_ANSWER:
        case AnswerPageContext.COHORT_ANSWER:
        case AnswerPageContext.CONV_ASSIST_ANSWER:
            return PageContext.ANSWER;
        case AnswerPageContext.PINBOARD_VIZ:
        case AnswerPageContext.PINBOARD_VIZ_EDIT_MODAL:
        case AnswerPageContext.SPOTIQ_VIZ:
        case AnswerPageContext.SPOTIQ_VIZ_EDIT_MODAL:
        case AnswerPageContext.EXPLORE:
        case AnswerPageContext.PRESENT:
            return PageContext.LIVEBOARD;
        default:
            return PageContext.UNMAPPED;
    }
};

/** Derives entry_path (query_initiated only) from page context + answer mode. */
export const mapEntryPath = (
    pageContext?: AnswerPageContext | null,
    answerMode?: AnswerMode | null,
): EntryPath => {
    if (
        pageContext === AnswerPageContext.CONV_ASSIST_ANSWER ||
        answerMode === AnswerMode.CONV_ASSIST_ANSWER_EDIT
    ) {
        return EntryPath.SPOTTER_EDIT;
    }
    if (
        pageContext === AnswerPageContext.PINBOARD_VIZ_EDIT_MODAL ||
        answerMode === AnswerMode.PINBOARD_TILE
    ) {
        return EntryPath.LIVEBOARD_EDIT;
    }
    if (pageContext === AnswerPageContext.SAVED_ANSWER) {
        return EntryPath.SAVED_ANSWER_EDIT;
    }
    return EntryPath.DIRECT;
};

/** Maps the answer's DisplayMode to the analytics viz_type; '' when unmapped. */
export const mapVizType = (displayMode?: DisplayMode | null): VizType => {
    switch (displayMode) {
        case DisplayMode.ChartMode:
            return VizType.CHART;
        case DisplayMode.TableMode:
            return VizType.TABLE;
        case DisplayMode.HeadlineMode:
            return VizType.HEADLINE;
        default:
            return VizType.UNMAPPED;
    }
};

interface CommonPropertiesArgs {
    queryId: string;
    queryableDataSource?: QuerableDataSourceType | null;
    pageContext?: AnswerPageContext | null;
    worksheetId?: string;
    answerSessionId?: string;
}

const buildCommonProperties = ({
    queryId,
    queryableDataSource,
    pageContext,
    worksheetId,
    answerSessionId,
}: CommonPropertiesArgs): PropertyMap => ({
    query_id: queryId,
    cdw_type: mapCdwType(queryableDataSource),
    page_context: mapPageContext(pageContext),
    worksheet_id: worksheetId ?? '',
    // Per-answer-page session (not the global maskedJsessionId).
    answer_session_id: answerSessionId ?? '',
});

export interface QueryInitiatedArgs {
    queryId: string;
    queryableDataSource?: QuerableDataSourceType | null;
    pageContext?: AnswerPageContext | null;
    answerMode?: AnswerMode | null;
    worksheetId?: string;
    answerSessionId?: string;
    vizType?: VizType;
}

export const buildQueryInitiatedEventProperties = ({
    queryId,
    queryableDataSource,
    pageContext,
    answerMode,
    worksheetId,
    answerSessionId,
    vizType,
}: QueryInitiatedArgs): PropertyMap => ({
    ...buildCommonProperties({
        queryId,
        queryableDataSource,
        pageContext,
        worksheetId,
        answerSessionId,
    }),
    entry_path: mapEntryPath(pageContext, answerMode),
    viz_type: vizType ?? VizType.UNMAPPED,
});

export interface QueryCancelledArgs {
    queryId: string;
    queryableDataSource?: QuerableDataSourceType | null;
    pageContext?: AnswerPageContext | null;
    cancelType: CancelType;
    triggerAction: TriggerAction;
    answerMode?: AnswerMode | null;
    worksheetId?: string;
    answerSessionId?: string;
    vizType?: VizType;
}

export const buildQueryCancelledEventProperties = ({
    queryId,
    queryableDataSource,
    pageContext,
    cancelType,
    triggerAction,
    answerMode,
    worksheetId,
    answerSessionId,
    vizType,
}: QueryCancelledArgs): PropertyMap => ({
    ...buildCommonProperties({
        queryId,
        queryableDataSource,
        pageContext,
        worksheetId,
        answerSessionId,
    }),
    cancel_type: cancelType,
    trigger_action: triggerAction,
    entry_path: mapEntryPath(pageContext, answerMode),
    viz_type: vizType ?? VizType.UNMAPPED,
});

// Counts phrases with at least one non-empty token (skips the trailing
// UNDEFINED_PHRASE placeholder). Phrase granularity so an in-phrase edit
// ("Order Date daily" -> "Order Date") doesn't read as a remove.
const countMeaningfulPhrases = (phrases?: SagePhrase[] | null): number => {
    if (!phrases) return 0;
    return phrases.filter(phrase => phrase.tokens?.some(token => token?.text))
        .length;
};

/** Classifies an implicit cancel by the change in meaningful-phrase count. */
export const getQueryCancellationTriggerForTokenChange = (
    submittedSnapshot?: SagePhrase[] | null,
    currentPhrases?: SagePhrase[] | null,
): TriggerAction => {
    if (!submittedSnapshot || !currentPhrases) {
        return TriggerAction.TOKEN_EDITED;
    }
    const before = countMeaningfulPhrases(submittedSnapshot);
    const after = countMeaningfulPhrases(currentPhrases);
    if (after > before) return TriggerAction.TOKEN_ADDED;
    if (after < before) return TriggerAction.TOKEN_REMOVED;
    return TriggerAction.TOKEN_EDITED;
};

export const useQueryInitiatedMetric = (
    metricsService: MetricsService,
    pageContext: AnswerPageContext | null | undefined,
    answerMode: AnswerMode | null | undefined,
    answerSessionId: string,
    vizType: VizType,
): ((queryId: string) => void) => {
    const client = useApolloClient();
    return React.useCallback(
        (queryId: string) => {
            // Lazily load answer-service so importing this util (e.g. for the
            // enums/mappers) doesn't pull the heavy answer-service graph in at
            // module load. Matches the lazy-import pattern already used in
            // metrics-service / lifecycle-hooks.
            const emitQueryInitiated = async () => {
                const {
                    getCachedQueryableDataSource,
                    getCachedWorksheetId,
                } = await import('/@services/answer/answer-service');
                const queryableDataSource = getCachedQueryableDataSource(
                    client,
                    answerSessionId,
                );
                const worksheetId = getCachedWorksheetId(
                    client,
                    answerSessionId,
                );
                metricsService.collectEvent(
                    new UserAction(userActionTypeEnum.QUERY_INITIATED),
                    buildQueryInitiatedEventProperties({
                        queryId,
                        queryableDataSource,
                        pageContext,
                        answerMode,
                        worksheetId,
                        answerSessionId,
                        vizType,
                    }),
                );
            };
            emitQueryInitiated().catch(() => {
                // Best-effort analytics — never throw on a deferred-import
                // failure.
            });
        },
        [
            answerSessionId,
            answerMode,
            client,
            metricsService,
            pageContext,
            vizType,
        ],
    );
};
